import { GasProductItem, GasProductTitle } from "./components/GasProductItem";
import { RefundPolicyNote } from "@components/payment/TapPayCompliance";
import { XStack, Text, Form, Button, Stack } from "tamagui";
import { colors } from "@styles/colors";
import { GasCard, GasLabel, SkinBottomBar, SkinDivider, isGasSkinV2 } from "./skin";
import { RiMapPin2Line, RiTimeLine, RiPhoneLine, RiCoupon3Line, RiCoinsLine, RiCoinLine, RiAddLine, RiSubtractLine, RiShoppingBag3Line, RiBillLine, RiFileList3Line } from "react-icons/ri";
import { YStack } from "tamagui";
import FormItem from "@components/form/FormItem";
import { useForm, FormProvider, FieldErrors } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { invoice, phoneNumber, taxIdNumber, invoiceCarrier } from "./GasShopFormItem";
import GasOrderSummary from "./components/GasOrderSummary";
import { useEffect, useRef, useState } from "react";
import { Product, useOrder } from "@utils/store/cart/useOrder";
import { useUtilsStore } from "@utils/store/utils/useUtilsTool";
import { useNavigate } from "react-router-dom";
import { delProductItem, delDraftOrder, updateDraftOrder, submitOrder, updateProductItem } from "@components/product/API/handleProductAPI";
import { AUTO_POPULATED_FLAG, MONTHLY_CUSTOMER_TYPE } from "./products";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { validateTaiwanPhone } from "@components/form/validate/vaildateTaiwanPhone";
import { validateTaxIdNumber } from "@components/form/validate/validateTaxIdNumber";
import { validateInvoiceCarrier } from "@components/form/validate/validateInvoiceCarrier";
import { AddressSwitchMenu } from "@components/address/AddressSwitchMenu";
import { PayMethodSwitchMenu } from "@components/PayMethodSwitchMenu";
import { CouponSwitchMenu } from "@components/CouponSwitchMenu";
import api from "@services/axios/apiManager";
import InlineLoading from "@components/loading/InlineLoading";
import { formatGasTitle } from "@utils/func/gasTitle";
import { buildGasCouponDiscountMap, discountedLineTotal } from "./utils/couponDiscount";
import PointsRedeemSection from "./components/PointsRedeemSection";
import { useCartPointsRedeem } from "./utils/useCartPointsRedeem";
import { track, clearEntrySource } from "@lib/tracker";
import { isPracticeMode } from "@utils/practice/practiceMode";
import { paymentSelectionLabel, paymentSelectionToRef } from "./utils/paymentSelection";
import { isMeteredCustomerType, shouldShowPaymentStep } from "@utils/func/billingCustomerType";
import { appToast } from "@components/Toast";
import { fetchCustomerTaxId, decideTaxIdPrefill } from "@utils/func/customerTaxId";
import { useNeedPhoneSetup } from "@screen/Kiosk/useNeedPhoneSetup";
import OtpCard from "@screen/Kiosk/components/OtpCard";
import { useKiosk } from "@screen/Kiosk/useKiosk";
import { shouldShowOtpCard, shouldBlockSubmit } from "@screen/Kiosk/otpPolicy";
import { useWriteBackVerifiedPhone } from "@screen/Kiosk/useWriteBackVerifiedPhone";
import { checkoutOtpPhone, shouldMountCheckoutOtpCard } from "@screen/Kiosk/checkoutOtpPhone";

export const GasShopCart = () => {
  const navigate = useNavigate();
  const skinV2 = isGasSkinV2();
  const { setLoading } = useUtilsStore();
  /**
   * V2 的送單進度改由底部主要按鈕自己轉圈，不拉全域的 <Loading /> 全螢幕遮罩——
   * 那層遮罩會把整個畫面蓋白，送單成功導頁時就變成「閃一下白 → 成功頁」的硬切。
   */
  const [submitting, setSubmitting] = useState(false);
  const {
    productList,
    setOrderState,
    draftOrderId,
    deliveryTime,
    clearProducts,
    syncDraft,
    customerCouponId,
    setCustomerCouponId,
    pendingCouponId,
    setPendingCouponId,
    couponDiscount,
    updateQuantity,
    removeProduct,
    hideAmountOnPending,
    setHideAmountOnPending,
    deliveryAreas,
    paymentSelection,
    setAllowElectronicPay,
    setIsMonthlyCustomer,
    setIsMeteredCustomer,
    isMeteredCustomer,
  } = useOrder();

  // 帳戶有沒有點數可折 —— 沒有就整個「點數折抵」區塊不出現（不做「餘額 0 點」的空欄位）
  const { empty: pointsEmpty } = useCartPointsRedeem();

  // 從優惠券頁「點券去下單」帶來的待套用券：購物車載入後套用一次，並清掉 pending。
  useEffect(() => {
    if (pendingCouponId) {
      setCustomerCouponId(pendingCouponId);
      setPendingCouponId(null);
    }
  }, [pendingCouponId]);

  // app_tracker：進到購物車頁視為漏斗「cart」步驟（只在 mount 時送一次）
  useEffect(() => {
    track("funnel_step", { step: "cart" });
  }, []);
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity, userData, setChooseIdentity, setUserData, accountPhoneLoaded, setAccountPhoneLoaded } = useUser();
  /**
   * 帳號沒有手機號碼（LINE 推播邀請註冊進來的客戶）→ 下單前先補登一支並驗證。
   * 與結算頁的 needCheckoutOtp（號碼有但沒驗過）是兩件事，見 Kiosk/phoneSetupPolicy.ts。
   */
  const needPhoneSetup = useNeedPhoneSetup();
  // 補登區塊一旦出現，本頁內就保持掛載，不隨 needPhoneSetup 翻轉而卸載。
  // 補登成功時會先把號碼寫回 userData（needPhoneSetup 翻 false），再通知頁面；兩個更新若不在
  // 同一次 render，中間那一瞬間條件全為 false，元件被卸掉又重新掛上，內部狀態歸零、
  // 回到輸入手機的第一步（dev 實測過）。用 ref 鎖住「本頁曾經需要補登」即可避免。
  const phoneSetupShownRef = useRef(false);
  if (needPhoneSetup) phoneSetupShownRef.current = true;
  const showPhoneSetup = needPhoneSetup || phoneSetupShownRef.current;
  // 沒配到就當新客戶：首次加入設定（含 pendingReview）已於訂購入口的 FirstJoinSetup 完成，結帳頁保持乾淨。
  const [showAddressSheet, setShowAddressSheet] = useState(false);
  const [updatingLineId, setUpdatingLineId] = useState<string | null>(null);
  const [isOpenNow, setIsOpenNow] = useState(true);
  // 平台層級設定（PW 後台控制）：是否顯示發票填寫區。opt-in：未設定 / 未取得設定時一律不顯示。
  const [showInvoice, setShowInvoice] = useState(false);
  const [allowElecPay, setAllowElecPay] = useState(false);
  // bootstrap 已回應（不論成敗）。統編自動帶入必須等這個為 true 才觸發，
  // 避免「bootstrap 尚未回應就先帶入統編」的空窗（showInvoice 初值已是 false，仍需等設定確定）。
  const [bootstrapLoaded, setBootstrapLoaded] = useState(false);
  // 月結客戶（cis.customer_type === 月結客戶）→ 付款方式顯示「月結」。
  const [isMonthly, setIsMonthly] = useState(false);
  // 這張單有沒有走過「付款方式」那一步。月結／走表客戶不會走到，付款方式由後端依
  // 客戶類型推導（monthly_settlement／device_metered），確認頁維持原本的單一顯示。
  const hasPaymentStep = shouldShowPaymentStep({
    allowElectronicPay: allowElecPay,
    isMonthlyCustomer: isMonthly,
    isMeteredCustomer,
  });
  // null = 尚未載入；載入後若為 0 且未選任何券，就隱藏整個「折扣優惠」欄位
  const [availableCouponCount, setAvailableCouponCount] = useState<number | null>(null);
  // 自動帶券決策是否已完成（CouponSwitchMenu 回報）；完成前不讓 GasOrderSummary 算費，
  // 避免 preview 先用「無券」算原價、auto-select 後才折扣造成閃動。
  const [couponResolved, setCouponResolved] = useState(false);
  // submit 成功會主動 navigate 走，且 clearProducts() 會把 productList 清空，
  // 這個 ref 用來告訴下面的「productList 空了就回 products」useEffect：不是使用者手動清空，別搶 navigate
  const submittingRef = useRef(false);

  //@ 0. 獲取營業狀態
  const fetchBootstrap = async () => {
    try {
      const res = await api.get("/app-api/platform-order-drafts/bootstrap", {
        headers: {
          Authorization: `Bearer ${cusJwtToken}`,
          "X-Context": buildXContextHeader(chooseIdentity),
        },
      });
      if (res?.data?.data) {
        const data = res.data.data;
        const primarySupplierId = chooseIdentity?.primary_supplier?.supplier_id;
        const targetData = data?.available_suppliers.find((item: any) => item.supplier_id === primarySupplierId);
        if (targetData) {
          setIsOpenNow(targetData.is_open_now ?? true);
          // 發票區開關（PW 後台 platform_setting）：=== true 才開，安全預設關
          setShowInvoice(targetData.platform_setting?.show_invoice === true);
          // 電子付款開關（PW 後台 platform_setting）：=== true 才開，安全預設關
          setAllowElecPay(targetData.platform_setting?.enable_electronic_payment === true);
          setIsMonthly(targetData.customer_type === MONTHLY_CUSTOMER_TYPE);
          // 同步到 store：配送時間頁據此決定要不要進「付款方式」步驟、PageWrapper 的返回也看它
          setAllowElectronicPay(targetData.platform_setting?.enable_electronic_payment === true);
          setIsMonthlyCustomer(targetData.customer_type === MONTHLY_CUSTOMER_TYPE);
          setIsMeteredCustomer(isMeteredCustomerType(targetData.customer_type));
          // 隱藏金額開關：=== true 才隱藏（下訂流程全程不顯示價格）
          setHideAmountOnPending(targetData.hide_amount_on_pending === true);
        }
        
        //-- 同步地址資訊
        if (chooseIdentity && data?.available_addresses?.length > 0) {
          const newAddresses = data.available_addresses;
          const newChooseAddress = chooseIdentity.choose_address || newAddresses[0];

          // 只有在地址清單或所選地址真的不同時才更新，避免無限 re-render
          const isAddressListSame = JSON.stringify(chooseIdentity.address) === JSON.stringify(newAddresses);
          const isChooseAddressSame = JSON.stringify(chooseIdentity.choose_address) === JSON.stringify(newChooseAddress);

          if (!isAddressListSame || !isChooseAddressSame) {
            setChooseIdentity({
              ...chooseIdentity,
              address: newAddresses,
              choose_address: newChooseAddress,
            });
          }
        }
      }
    } catch (error) {
      console.error("Fetch bootstrap error:", error);
    } finally {
      setBootstrapLoaded(true);
    }
  };

  useEffect(() => {
    fetchBootstrap();
  }, [cusJwtToken, chooseIdentity?.primary_supplier?.supplier_id]);

  const handleUpdateQuantity = async (p: Product, newQty: number) => {
    if (updatingLineId || !draftOrderId) return;

    //-- 樂觀更新: 先更新本地狀態
    if (newQty <= 0) {
      removeProduct(p.line_id!);
    } else {
      updateQuantity(p.line_id!, newQty);
    }

    setUpdatingLineId(p.line_id!); //-- 行內 spinner；不開全域遮罩，避免改個數量整頁被蓋掉
    try {
      const type = p.gas_type ? "gas" : "commodity";
      if (newQty <= 0) {
        if (productList?.length === 1) {
          const res = await delDraftOrder({
            draftId: draftOrderId || "",
            jwtToken: cusJwtToken || "",
            xContext: buildXContextHeader(chooseIdentity) || "",
          });
          if (res?.state === "success") {
            clearProducts();
          }
        } else {
          const res = await delProductItem({
            type,
            draftId: draftOrderId || "",
            lineId: p.line_id || "",
            jwtToken: cusJwtToken || "",
            xContext: buildXContextHeader(chooseIdentity) || "",
          });
          if (res?.state === "success") {
            if (res.data?.data?.draft) {
              syncDraft(res.data.data.draft);
            } else {
              setOrderState("loading");
            }
          }
        }
      } else {
        const postData = { ...p };
        delete (postData as any).line_id;
        delete (postData as any).totalPrice;
        postData.qty = newQty;

        const res = await updateProductItem({
          xContext: buildXContextHeader(chooseIdentity) || "",
          jwtToken: cusJwtToken || "",
          type,
          draftId: draftOrderId || "",
          lineId: p.line_id!,
          body: postData,
        });
        if (res?.state === "success") {
          if (res.data?.data?.draft) {
            syncDraft(res.data.data.draft);
          } else {
            setOrderState("loading");
          }
        }
      }
    } catch (error) {
      console.error("handleUpdateQuantity error:", error);
    } finally {
      setUpdatingLineId(null);
    }
  };

  //@ 1. 初始獲取草稿詳細資訊
  const fetchDraftDetail = async () => {
    if (!draftOrderId || !cusJwtToken) return;
    try {
      const res = await api.get(`/app-api/platform-order-drafts/${draftOrderId}`, {
        headers: {
          Authorization: `Bearer ${cusJwtToken}`,
          "X-Context": buildXContextHeader(chooseIdentity),
        },
      });
      if (res?.data?.data) {
        const draft = res.data.data;
        // 同步至 Zustand store
        syncDraft(draft);

        // 同步至 Form (如果有對應的值)
        if (draft.contact_phone) setValue("phoneNumber", draft.contact_phone);
        if (draft.payment_method_ref) setValue("payment_method_ref", draft.payment_method_ref);
        // 捐贈選項暫時隱藏：草稿殘留的 donate 不回填（維持預設 carrier），避免選不到卻送出捐贈
        if (draft.invoice_type && draft.invoice_type !== "donate") setValue("invoice_type", draft.invoice_type);
        if (draft.invoice_carrier) setValue("invoiceCarrier", draft.invoice_carrier);
        if (draft.invoice_tax_id) setValue("taxIdNumber", draft.invoice_tax_id);
      }
    } catch (error) {
      console.error("Fetch draft detail error:", error);
    }
  };

  useEffect(() => {
    fetchDraftDetail();
  }, [draftOrderId, cusJwtToken]);

  //@ 建立 Form
  const formSchema = z
    .object({
      deliveryAddress: z.string().trim().min(1, { message: "請選擇配送地址" }),
      phoneNumber: z
        .string()
        .trim()
        .min(1, { message: "請填寫聯絡電話" })
        .refine(validateTaiwanPhone, { message: "請輸入有效的手機號碼或市話 (含區碼)" }),
      // 發票類型：平台關閉發票區(showInvoice=false)時整欄不給選 → 不驗證（見 superRefine）。
      invoice_type: z.string().optional(),
      taxIdNumber: z
        .string()
        .optional()
        .refine((val) => !val || validateTaxIdNumber(val), { message: "請輸入有效的統一編號" }),
      invoiceCarrier: z
        .string()
        .optional()
        .refine((val) => !val || validateInvoiceCarrier(val), { message: "請輸入有效的載具條碼" }),
      payment_method_ref: z.string().min(1, { message: "請選擇支付方式" }),
    })
    .superRefine((data, ctx) => {
      // 平台關閉發票區 → 完全沒有發票選項，跳過所有發票驗證。
      if (!showInvoice) return;
      if (!data.invoice_type) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: "請選擇發票類型", path: ["invoice_type"] });
        return;
      }
      if (data.invoice_type === "carrier" && (!data.invoiceCarrier || data.invoiceCarrier.trim() === "")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "請輸入載具條碼",
          path: ["invoiceCarrier"],
        });
      }
      if (data.invoice_type === "tax_id" && (!data.taxIdNumber || data.taxIdNumber.trim() === "")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "請輸入統一編號",
          path: ["taxIdNumber"],
        });
      }
    });
  type FormData = z.infer<typeof formSchema>;

  const methods = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      deliveryAddress: chooseIdentity?.choose_address?.full_address || "",
      phoneNumber: userData?.phone || "",
      invoice_type: "other",
      taxIdNumber: "",
      invoiceCarrier: "",
      payment_method_ref: "cash_on_delivery",
    },
  });

  const {
    formState: { errors },
    setValue,
    watch,
  } = methods;

  const currentInvoiceType = watch("invoice_type");

  /**
   * 帳號有手機但沒驗證過（後端 phone_verified=false，WaitingArrangementGate 已寫進 useKiosk）
   * → 結算頁內嵌簡訊驗證，與 /kiosk/confirm 同一元件。帳號沒手機時由上面的補登區塊處理（已含驗證），兩者只出現一個。
   * 驗證對象用帳號手機（不跟「聯絡電話」欄位逐字連動，避免邊打字邊寄簡訊）；改號碼走卡內「修改號碼」。
   */
  const { needCheckoutOtp, otpVerified, setOtpVerified, setPhoneVerifiedTicket } = useKiosk();
  const [otpCode, setOtpCode] = useState<string[]>(["", "", "", "", ""]);
  // 驗證對象＝帳號手機；聯絡電話欄位（phoneNumber）改了不影響驗證（與 Kiosk 同一規則）
  const [otpPhone, setOtpPhone] = useState<string>(checkoutOtpPhone({ accountPhone: userData?.phone }));
  useEffect(() => {
    if (!otpPhone && userData?.phone) setOtpPhone(checkoutOtpPhone({ accountPhone: userData.phone }));
  }, [userData?.phone, otpPhone]);
    // 寫回帳號手機失敗（例如 409 號碼被其他帳號使用）：帳號沒手機的客戶仍可下單，聯絡電話＝剛驗證的號碼，卡片顯示提示。
  // onFailed 在非同步回應中呼叫，讀 ref 不讀 render 快照。
  const [otpNote, setOtpNote] = useState("");
  const showPhoneSetupRef = useRef(showPhoneSetup);
  showPhoneSetupRef.current = showPhoneSetup;
  const onWriteBackFailed = (r: { kind: string; message: string }) => {
    if (!showPhoneSetupRef.current) return; // 有手機客戶維持原 toast
    setOtpNote(r.kind === "phone_taken" ? "這支手機已被其他帳號使用，訂單會用這支號碼聯絡您" : `${r.message}，訂單會用這支號碼聯絡您`);
    return true;
  };
  const writeBackVerifiedPhone = useWriteBackVerifiedPhone((saved) => setValue("phoneNumber", saved, { shouldValidate: true }), onWriteBackFailed);
  // 帳號資料已載入（號碼有值也算）；號碼不是手機時卡片仍要掛（開在「修改號碼」），否則送出被擋又沒地方驗證
  const otpAccountLoaded = accountPhoneLoaded || !!userData?.phone;
  const showOtpCard = shouldMountCheckoutOtpCard({
    showPhoneSetup,
    needOtpCard: shouldShowOtpCard({ needCheckoutOtp, otpVerified }),
    accountLoaded: otpAccountLoaded,
  });

  // 發票區被平台設定關閉時，整欄不給選（等於沒有發票），清掉殘留欄位；
  // 送單時 invoice 全帶 null（見 onSubmit）。
  useEffect(() => {
    if (!showInvoice) {
      setValue("invoiceCarrier", "");
      setValue("taxIdNumber", "");
    }
  }, [showInvoice, setValue]);

  useEffect(() => {
    if (chooseIdentity?.choose_address?.full_address) {
      setValue("deliveryAddress", chooseIdentity.choose_address.full_address);
    }
  }, [chooseIdentity, setValue]);

  // 統編欄位為空時，自動帶入註冊時留存的統編（使用者仍可修改）。
  // 只在仍為空時 setValue，避免蓋掉草稿回填或使用者輸入。
  useEffect(() => {
    if (!bootstrapLoaded || !showInvoice) return;
    let cancelled = false;
    (async () => {
      const registered = await fetchCustomerTaxId(buildXContextHeader(chooseIdentity) || undefined);
      const action = decideTaxIdPrefill({
        bootstrapLoaded: true, // effect 進入點已保證
        showInvoice,
        currentType: methods.getValues("invoice_type") || "",
        currentCarrier: methods.getValues("invoiceCarrier") || "",
        currentTax: methods.getValues("taxIdNumber") || "",
        registered,
      });
      if (!cancelled && action) {
        // fill-and-select：使用者尚未動過發票區 → 連「統編」radio 一起選上，
        // 否則統編輸入框不會顯示、帶入了也看不到。
        if (action === "fill-and-select") setValue("invoice_type", "tax_id");
        setValue("taxIdNumber", registered);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapLoaded, showInvoice, chooseIdentity?.context_id]);

  // userData.phone 來自 customer.main_phone（HomePage 拉的 /customer/home）
  // 若使用者沒先逛 home 直接到 cart，userData 可能 null；之後 sync 進來時補帶
  useEffect(() => {
    const phone = userData?.phone;
    const current = methods.getValues("phoneNumber");
    if (phone && !current) {
      setValue("phoneNumber", phone);
    }
  }, [userData?.phone, methods, setValue]);

  // 直接從 LIFF 進 cart（沒先逛 HomePage）時 userData 是 null，phone 拿不到 → 主動補抓。
  // accountPhoneLoaded 也是離開條件：LINE 推播邀請註冊的客戶帳號本來就沒有手機，
  // 抓回來 phone 依然是空的，只看 userData?.phone 會分不出「還沒載入」與「真的沒有」
  //（結帳補登手機的閘門靠這個旗標判斷，見 Kiosk/phoneSetupPolicy.ts）。
  useEffect(() => {
    if (!cusJwtToken || !chooseIdentity?.context_id || userData?.phone || accountPhoneLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/app-api/customer/home", {
          headers: { Authorization: `Bearer ${cusJwtToken}`, "X-Context": buildXContextHeader(chooseIdentity) },
        });
        if (cancelled) return;
        const data = res?.data?.data;
        if (data?.customer) {
          setUserData({ name: data.customer.customer_name, phone: data.customer.main_phone });
        }
        // 成功回來才標記載入完成：失敗時維持 false，補登閘門不會被誤觸發（fail-open）。
        setAccountPhoneLoaded(true);
      } catch (error) {
        console.error("Fetch customer/home for phone fallback error:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cusJwtToken, chooseIdentity?.context_id, userData?.phone, accountPhoneLoaded, setUserData, setAccountPhoneLoaded]);

  //@ 處理滾動至錯誤位置
  const onInvalid = (errors: FieldErrors<FormData>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const element = document.getElementsByName(firstErrorKey)[0] || document.getElementById(firstErrorKey);

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else {
        const fallbackElement = document.querySelector(`[id*="${firstErrorKey}"]`);
        fallbackElement?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const onSubmit = async (data: FormData) => {
    if (shouldBlockSubmit({ needCheckoutOtp, otpVerified })) {
      appToast.error("請先完成簡訊驗證");
      return;
    }
    if (deliveryTime.type === "scheduled" && (!deliveryTime.date || !deliveryTime.slotId)) {
      alert("請選擇預約配送日期與時段");
      return;
    }
    // 電子付款開啟 → 付款方式由「付款方式」步驟決定；沒選過就帶回去選，不預設貨到付款
    const selectedPaymentRef = hasPaymentStep ? paymentSelectionToRef(paymentSelection) : null;
    if (hasPaymentStep && !selectedPaymentRef) {
      appToast.error("請選擇付款方式");
      navigate("/gasShop/payment");
      return;
    }

    if (skinV2) setSubmitting(true);
    else setLoading(true);

    // 格式化發票資訊
    let invoice_carrier: string | null = null;
    let invoice_tax_id: string | null = null;

    if (data.invoice_type === "carrier") {
      invoice_carrier = data.invoiceCarrier || null;
    } else if (data.invoice_type === "tax_id") {
      invoice_tax_id = data.taxIdNumber || null;
    }

    // 平台關閉發票區 → 完全不帶發票（invoice 全 null），而非預設捐贈。
    const payload = {
      customer_address_id: chooseIdentity?.choose_address?.customer_address_id,
      contact_phone: data.phoneNumber,
      payment_method_ref: selectedPaymentRef ?? data.payment_method_ref,
      // 平台關閉發票區、或客戶選「其他」→ 發票欄位全帶 null（開一般 B2C 發票）。
      invoice_type: showInvoice && data.invoice_type !== "other" ? data.invoice_type : null,
      invoice_carrier: showInvoice ? invoice_carrier : null,
      invoice_tax_id: showInvoice ? invoice_tax_id : null,
      delivery_mode: deliveryTime.type === "scheduled" ? "reserve" : "immediate",
      delivery_date: deliveryTime.date,
      delivery_slot_id: deliveryTime.slotId,
      delivery_slot_source: deliveryTime.slotSource,
      customer_coupon_id: customerCouponId || null,
      // TODO(points-api): 點數折抵目前只在前端計算與顯示，尚未送給後端 —— 端點與 DTO 都還沒有，
      // 貿然多送一個欄位可能被後端的 whitelist 擋成 400。端點到位後在這裡與下方 submit body 各補兩個欄位：
      //   point_redeem:        { stored_gas, account }  一般訂單，折這麼多點（後端須重算，不可信任前端）
      //   point_redeem_intent: { stored_gas, account }  隱藏金額的訂單，只表示「有金額後幫我折」，
      //                                                 由後端在定價時依餘額上限套用
    };

    // 1. 更新草稿
    const updateRes = await updateDraftOrder({
      draftId: draftOrderId || "",
      body: payload,
      jwtToken: cusJwtToken || "",
      xContext: buildXContextHeader(chooseIdentity) || "",
    });

    if (updateRes.state === "success") {
      // 2. 送出訂單
      // customer_coupon_id 必須在 submit body 顯式帶；後端 platform-orders.service 只看 request body
      // 不會 fallback 到 draft.customer_coupon_id（spec §8.2）
      const submitClientRequestId = crypto.randomUUID();
      track("order_submit_click", { client_request_id: submitClientRequestId });
      const submitRes = await submitOrder({
        body: {
          draft_id: draftOrderId || "",
          client_request_id: submitClientRequestId,
          customer_coupon_id: customerCouponId || null,
          // TODO(points-api): 見上方 payload 的說明，待後端點數端點到位後在此補 point_redeem。
        },
        jwtToken: cusJwtToken || "",
        xContext: buildXContextHeader(chooseIdentity) || "",
      });

      if (submitRes.state === "success") {
        const newOrderId = submitRes.data?.data?.platform_order_id;
        track("order_success_view", { client_request_id: submitClientRequestId });
        clearEntrySource(); // 下單成功，離開漏斗，清掉 entry_source 附掛狀態
        // 這次的簡訊驗證只算這一張單：清掉，下一張單照後端 phone_verified 重新判斷。
        // 不用 resetCheckout——那會一併清掉 Kiosk 的聯絡電話、發票、付款偏好等，與本頁無關。
        setOtpVerified(false);
        setPhoneVerifiedTicket("");
        // 標記為主動導頁中，避免 clearProducts() 後下面的「productList 空了就回 products」useEffect 把我們搶過去
        submittingRef.current = true;
        clearProducts();
        // 送單成功 → 重置 flag，下次回 products 用新的 last_order 重新帶入
        sessionStorage.removeItem(AUTO_POPULATED_FLAG);
        // 先進下單成功頁（倒數後自動轉訂單詳情）；沒拿到 orderId 就退回訂購頁，避免 navigate(undefined)
        navigate(newOrderId ? "/gasShop/done" : "/gasShop/products", newOrderId ? { state: { orderId: newOrderId } } : undefined);
      }
    }
    setSubmitting(false);
    setLoading(false);
  };

  useEffect(() => {
    if (submittingRef.current) return; // 送單成功的 clearProducts() 不該觸發回退到 products
    // 練習模式：草稿有偶發去同步會讓 productList 瞬間變空，若照樣退回 products 會形成迴圈。
    // 練習假後端會在抓草稿時保底補回品項，所以這裡不退回，改重新抓一次草稿把品項帶回來。
    if (isPracticeMode()) {
      if (productList?.length <= 0 && draftOrderId) fetchDraftDetail();
      return;
    }
    if (productList?.length <= 0) {
      navigate("/gasShop/products");
    }
  }, [productList]);

  // 優惠券折扣攤分（GasOrderSummary 取得 preview 後寫入 store 的 couponDiscount）；
  // 用於「訂購項目」清單把折扣折在瓦斯價上，與下方訂單摘要一致。
  // couponDiscount === null 代表 preview 尚未回（pending）：品項價顯示 loading，
  // 避免一進頁面先閃原價、preview 回來才跳折後價。
  const discountPending = couponDiscount === null;
  const gasCouponDiscountMap = buildGasCouponDiscountMap(productList ?? [], couponDiscount ?? 0);
  // 訂購項目依公斤數排序（小→大）；非瓦斯排最後。折扣以 line_id 對應，排序不影響金額。
  const sortedProductList = [...(productList ?? [])].sort((a, b) => (a.kilogram ?? Infinity) - (b.kilogram ?? Infinity));

  // 可送出：營業中可立即下單；公休時只要已選好預約日期＋時段也可送出（預約配送）。
  const hasScheduledSlot = deliveryTime.type === "scheduled" && !!deliveryTime.date && !!deliveryTime.slotId;
  const canSubmit = isOpenNow || hasScheduledSlot;

  // 新客戶完整加入設定（含 pendingReview）已於訂購入口的 FirstJoinSetup 完成；結帳頁只保留每筆訂單欄位。
  // 帳號沒有手機時擋住送出，直到補登區塊驗證完成（送單一定要有可聯絡的門號）。
  // 帳號沒有手機 → 先補登；有手機但沒驗證過 → 先過結算頁簡訊驗證。兩者都要擋送出。
  const canOrder = canSubmit && !(needPhoneSetup && !otpVerified) && !shouldBlockSubmit({ needCheckoutOtp, otpVerified });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={methods.handleSubmit(onSubmit, onInvalid)} gap={skinV2 ? "$2" : "$4"} flex={1}>
        <YStack gap={15}>
          {/* 配送資訊：地址 / 時間 / 電話 合併為一張卡，用分隔線區隔 */}
          <GasCard id="deliveryAddress">
            <YStack gap={skinV2 ? 16 : "$3"}>
              {/* 配送地址 */}
              <YStack gap={skinV2 ? 0 : "$2"}>
                <XStack alignItems="center" justifyContent="space-between" onPress={() => setShowAddressSheet(true)}>
                  <GasLabel label="配送地址" icon={<RiMapPin2Line color={colors.primary} />} />
                </XStack>
                <AddressSwitchMenu themeColor="black" changeColor="primary" deliveryAreas={deliveryAreas} flush={skinV2} />
              </YStack>

              {!skinV2 && <Stack height={1} backgroundColor={colors.borderLight} />}

              {/* 配送時間 */}
              <YStack gap={skinV2 ? 0 : "$2"}>
                <GasLabel label="配送時間" icon={<RiTimeLine color={colors.primary} />} />
                <XStack ai="center" jc="space-between" gap="$2" px={skinV2 ? 0 : 10} minHeight={skinV2 ? 21 : undefined}>
                  {deliveryTime.type === "immediate" ? (
                    <XStack ai="baseline" gap="$2" flex={1}>
                      <Text fontSize={14} fontWeight="bold">
                        立即配送
                      </Text>
                      <Text fontSize={13} color={colors.gray}>
                        下單後將儘速為您安排配送人員
                      </Text>
                    </XStack>
                  ) : deliveryTime.type === "scheduled" && deliveryTime.date && deliveryTime.slotId ? (
                    <XStack ai="baseline" gap="$2" flex={1}>
                      <Text fontSize={14} fontWeight="bold">
                        {deliveryTime.date}
                      </Text>
                      <Text fontSize={13} color={colors.text}>
                        {deliveryTime.slotLabel}
                      </Text>
                    </XStack>
                  ) : (
                    <Text fontSize={14} color={colors.danger} flex={1} cursor="pointer" onPress={() => navigate("/gasShop/deliveryTime")}>
                      尚未選擇配送時間
                    </Text>
                  )}
                  <Text color={colors.primary} fontSize={14} fontWeight="bold" ml="$2" cursor="pointer" onPress={() => navigate("/gasShop/deliveryTime")}>
                    變更
                  </Text>
                </XStack>
              </YStack>

              {!skinV2 && <Stack height={1} backgroundColor={colors.borderLight} />}

              {/* 聯絡電話 */}
              <YStack gap={skinV2 ? 0 : "$2"}>
                <GasLabel label="聯絡電話" icon={<RiPhoneLine color={colors.primary} />} />
                <YStack id={phoneNumber.data?.id}>
                  <FormItem itemType={phoneNumber?.itemType} required={phoneNumber?.required} data={phoneNumber?.data} />
                </YStack>
              </YStack>
            </YStack>
          </GasCard>

          {showOtpCard && (
            <OtpCard
              code={otpCode}
              setCode={setOtpCode}
              phone={otpPhone || checkoutOtpPhone({ accountPhone: userData?.phone })}
              accountLoaded={otpAccountLoaded}
              alreadyVerified={otpVerified}
              noAccountPhone={showPhoneSetup}
              verifiedNote={otpNote}
              onChangePhone={setOtpPhone}
              onVerified={(phone, ticket) => {
                // 這張單用剛驗證的號碼聯絡（就算寫回帳號失敗也一樣）
                setValue("phoneNumber", phone, { shouldValidate: true });
                void writeBackVerifiedPhone(phone, ticket);
              }}
            />
          )}


          {/* V2：整段收進一張卡，「訂購項目」標題放卡內 —— 它原本是唯一浮在頁面背景上的區塊標題，
              深色字壓在藍色漸層上對比差，也跟其他區塊（標題都在白卡內）不一致。 */}
          {(() => {
            const renderItem = (item: Product, idx: number) => {
              const title = item?.gas_type ? formatGasTitle(item.gas_type, item.kilogram) : item?.display_name || "其他商品";
              // 優惠券折扣直接折在瓦斯價上，與下方訂單摘要一致
              const original = item.unit_price * item.qty;
              const shownPrice = discountedLineTotal(item, idx, gasCouponDiscountMap);
              const hasDiscount = shownPrice < original;
              return (
                <>
                  <GasProductTitle
                    title={title}
                    desc=""
                    price={shownPrice.toLocaleString()}
                    originalPrice={hasDiscount ? original.toLocaleString() : undefined}
                    priceLoading={discountPending}
                    hidePrice={hideAmountOnPending}
                    theme="basic"
                    dense={skinV2}
                    kilogram={item.kilogram}
                    tags={[
                      item.is_elevator ? "有電梯" : "無電梯",
                      item.floor === 0 ? "1F" : item.floor > 0 ? `${item.floor}F` : `B${-item.floor}`,
                      item.usage_location || "",
                      item.note || "",
                    ].filter(Boolean)}
                  />
                  {skinV2 ? <YStack height={6} /> : <hr />}
                  <XStack justifyContent="space-between" alignItems="center">
                    {/* 「移除」是次要操作，V2 收成小一號的一般字重（顏色維持原本的 danger 紅，不淡化），
                        並把 Tamagui Button 的預設高度（$true 約 44px）壓掉 —— 那個高度是這一列
                        跟上面商品列之間看起來空一大截的主因。 */}
                    <Button chromeless p={0} h={skinV2 ? 26 : undefined} onPress={() => handleUpdateQuantity(item, 0)}>
                      <Text color={colors.danger} fontSize={skinV2 ? 13 : 16} fontWeight={skinV2 ? "500" : "bold"}>
                        移除
                      </Text>
                    </Button>
                    <XStack ai="center" gap="$3">
                      <Button
                        size="$1"
                        circular
                        bg={colors.light}
                        borderColor={colors.gray100}
                        borderWidth={1}
                        disabled={!!updatingLineId}
                        onPress={() => handleUpdateQuantity(item, item.qty - 1)}>
                        <RiSubtractLine size={14} color={colors.gray} />
                      </Button>
                      <XStack width={20} jc="center" ai="center" position="relative">
                        <Text fontWeight="bold" fontSize={16} textAlign="center" opacity={updatingLineId === item.line_id ? 0.3 : 1}>
                          {item.qty}
                        </Text>
                        {updatingLineId === item.line_id && (
                          <Stack position="absolute">
                            <InlineLoading />
                          </Stack>
                        )}
                      </XStack>
                      <Button
                        size="$1"
                        circular
                        bg={colors.light}
                        borderColor={colors.gray100}
                        borderWidth={1}
                        disabled={!!updatingLineId}
                        onPress={() => handleUpdateQuantity(item, item.qty + 1)}>
                        <RiAddLine size={14} color={colors.gray} />
                      </Button>
                    </XStack>
                  </XStack>
                </>
              );
            };

            if (skinV2) {
              return (
                <GasCard>
                  <GasLabel label="訂購項目" icon={<RiShoppingBag3Line color={colors.primary} />} />
                  {sortedProductList.map((item: Product, idx: number) => (
                    <YStack key={item.line_id}>
                      {idx > 0 && <SkinDivider my={12} />}
                      {renderItem(item, idx)}
                    </YStack>
                  ))}
                </GasCard>
              );
            }

            return (
              <YStack gap={10}>
                <GasLabel label="訂購項目" icon={<RiShoppingBag3Line color={colors.primary} />} />
                {sortedProductList.map((item: Product, idx: number) => (
                  <GasCard key={item.line_id}>{renderItem(item, idx)}</GasCard>
                ))}
              </YStack>
            );
          })()}
          {/* 優惠券／支付方式／發票：V2 合成一張卡用分隔線隔開（同上方「配送資訊」卡的做法）。
              三張獨立卡各帶 18px 上下內距＋卡間 15px 間距，光留白就近 140px，而每張只放一行值。
              折扣優惠：沒有可用優惠券且未選券時整個欄位隱藏。 */}
          {(() => {
            const showCoupon = availableCouponCount === null || availableCouponCount > 0 || !!customerCouponId;
            const couponSection = (
              <>
                <GasLabel theme="green" label="優惠券" icon={<RiCoupon3Line color={colors.green} />} />
                <CouponSwitchMenu
                  themeColor="black"
                  changeColor="primary"
                  flush={skinV2}
                  amountAccent={skinV2}
                  selectId={customerCouponId || ""}
                  onLoaded={(count) => {
                    setAvailableCouponCount(count);
                    // 無券可帶時這段會被收起（CouponSwitchMenu 卸載），其 onAutoResolved 可能來不及呼叫，
                    // 這裡直接放行算費，避免訂單摘要卡在 loading。
                    if (count === 0) setCouponResolved(true);
                  }}
                  onAutoResolved={() => setCouponResolved(true)}
                />
              </>
            );
            const pointsSection = (
              <>
                <GasLabel theme="amber" label="點數折抵" icon={<RiCoinLine color={colors.amber} />} />
                <PointsRedeemSection intentOnly={hideAmountOnPending} />
              </>
            );
            // 電子付款開啟：付款方式在上一步（/gasShop/payment）選好，這裡只顯示＋「變更」回去改；
            // 關閉：維持原本的貨到付款／月結單一顯示（PayMethodSwitchMenu 不開電子選項）。
            const selectedPaymentText = paymentSelectionLabel(paymentSelection, isMonthly);
            const paySection = (
              <>
                <GasLabel label="支付方式" icon={<RiCoinsLine color={colors.primary} />} />
                {hasPaymentStep ? (
                  <XStack ai="center" jc="space-between" h={skinV2 ? 21 : 40} mt={skinV2 ? 0 : "$2"}>
                    {selectedPaymentText ? (
                      <Text fontSize={14} fontWeight="bold" flex={1} numberOfLines={1}>
                        {selectedPaymentText}
                      </Text>
                    ) : (
                      <Text fontSize={14} color={colors.danger} flex={1} cursor="pointer" onPress={() => navigate("/gasShop/payment")}>
                        尚未選擇付款方式
                      </Text>
                    )}
                    <Text color={colors.primary} fontSize={14} fontWeight="bold" ml="$2" cursor="pointer" onPress={() => navigate("/gasShop/payment")}>
                      變更
                    </Text>
                  </XStack>
                ) : (
                  <PayMethodSwitchMenu
                    themeColor="black"
                    changeColor="primary"
                    flush={skinV2}
                    monthly={isMonthly}
                    value={watch("payment_method_ref")}
                    onChange={(ref) => setValue("payment_method_ref", ref)}
                    cusJwtToken={cusJwtToken || ""}
                    elecEnabled={false}
                  />
                )}
              </>
            );
            // 發票區：受 supplier 平台設定 platform_setting.show_invoice 控制（PW 後台可關閉）
            const invoiceSection = (
              <>
                <GasLabel label="發票" icon={<RiBillLine color={colors.primary} />} />
                <YStack id={invoice.data?.id} mt={skinV2 ? 0 : "$3"}>
                  <FormItem itemType={invoice?.itemType} required={invoice?.required} data={invoice?.data} />
                </YStack>
                {currentInvoiceType === "carrier" && (
                  <YStack id={invoiceCarrier.data?.id} mt="$2">
                    <FormItem itemType={invoiceCarrier?.itemType} required={invoiceCarrier?.required} data={invoiceCarrier?.data} />
                  </YStack>
                )}
                {currentInvoiceType === "tax_id" && (
                  <YStack id={taxIdNumber.data?.id} mt="$2">
                    <FormItem itemType={taxIdNumber?.itemType} required={taxIdNumber?.required} data={taxIdNumber?.data} />
                  </YStack>
                )}
              </>
            );

            if (skinV2) {
              const sections = [
                ...(showCoupon ? [couponSection] : []),
                paySection,
                ...(showInvoice ? [invoiceSection] : []),
              ];
              return (
                <>
                  <GasCard>
                    {sections.map((section, idx) => (
                      <YStack key={idx}>
                        {idx > 0 && <YStack height={18} />}
                        {section}
                      </YStack>
                    ))}
                  </GasCard>
                  {/* 點數折抵自己一張卡：它有開關、可輸入、還會長出折抵金額，
                      和優惠券／支付方式那種「一行值 + 變更」的欄位不是同一種東西，
                      擠在同一張卡裡最高的那一段永遠是它。放在這張卡之後、訂單摘要之前——
                      券仍在它上面（折抵順序不變），而它緊貼著真正算錢的那張卡。

                      瓦斯行開啟隱藏金額時改走 intentOnly：那種單子的金額要接單後才有，
                      折抵算不出數字（連 GasOrderSummary 都不渲染、previewTotal 永遠是 null），
                      所以只收「要不要折」，由瓦斯行定價時套用。 */}
                  {!pointsEmpty && <GasCard>{pointsSection}</GasCard>}
                </>
              );
            }

            return (
              <>
                {showCoupon && <GasCard>{couponSection}</GasCard>}
                <GasCard>{paySection}</GasCard>
                {showInvoice && <GasCard>{invoiceSection}</GasCard>}
                {!pointsEmpty && <GasCard>{pointsSection}</GasCard>}
              </>
            );
          })()}
          {/* 結帳明細；瓦斯行開啟隱藏金額時整張訂單摘要不顯示（金額由瓦斯行接單確認後提供） */}
          {!hideAmountOnPending && (
            <GasCard>
              <GasLabel label="訂單摘要" icon={<RiFileList3Line color={colors.primary} />} />
              <GasOrderSummary couponResolved={couponResolved} />
            </GasCard>
          )}
          {/* 下單前揭露退換貨與取消政策（金流審核與消保法皆要求訂購前告知） */}
          <RefundPolicyNote mt={skinV2 ? 0 : "$5"} />
        </YStack>
        {skinV2 ? (
          <SkinBottomBar
            secondaryLabel="返回修改"
            onSecondary={() => navigate(hasPaymentStep ? "/gasShop/payment" : "/gasShop/deliveryTime")}
            primaryLabel={canSubmit ? "確認下單" : "尚未營業"}
            primaryDisabled={!canOrder || submitting}
            primaryLoading={submitting}
            onPrimary={methods.handleSubmit(onSubmit, onInvalid)}
          />
        ) : (
          <YStack backgroundColor={colors.bg} pt={8} style={{ position: "sticky", bottom: "0", width: "100%", zIndex: 100 }}>
            <XStack
              className={canOrder ? "gasshop-btn-gradient" : ""}
              backgroundColor={canOrder ? undefined : colors.gray100}
              borderRadius={16}
              px={20}
              py={17}
              alignItems="center"
              justifyContent="center"
              onPress={canOrder ? methods.handleSubmit(onSubmit, onInvalid) : undefined}
              opacity={canOrder ? 1 : 0.5}
              cursor={canOrder ? "pointer" : "default"}
              pressStyle={canOrder ? { opacity: 0.9 } : undefined}
              style={{ boxShadow: canOrder ? "0 4px 12px -6px rgba(33,150,243,.45)" : undefined }}>
              <XStack gap={10}>
                <Text color={canOrder ? "white" : colors.gray} fontSize={17} fontWeight="800">
                  {canSubmit ? "確認下單" : "尚未營業"}
                </Text>
              </XStack>
            </XStack>
          </YStack>
        )}
      </Form>
    </FormProvider>
  );
};

export default GasShopCart;
